import axios from "axios";
import { logger } from "@/lib/logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const MUTATING = new Set(["post", "put", "patch", "delete"]);
const CSRF_COOKIE = "csrf_token";
const CSRF_HEADER = "X-CSRF-Token";

function readCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 30_000, // Render free-tier cold starts can take 20-30s to wake up
});

/**
 * Convenience wrapper: GET <url> and return response.data directly.
 * Lets call sites use `.then(setter)` without an intermediate Promise-callback
 * variable inside React hooks (keeps useEffect/useCallback bodies dep-clean).
 */
export const getJson = (url, opts) => api.get(url, opts).then((response) => response.data);

// Attach CSRF header on every mutating request
api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (MUTATING.has(method)) {
    const token = readCookie(CSRF_COOKIE);
    if (token) config.headers[CSRF_HEADER] = token;
  }
  return config;
});

// On a 403 CSRF failure, bootstrap a token once and retry
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error.config || {};
    const isCsrf =
      error.response?.status === 403 &&
      /csrf/i.test(error.response?.data?.detail || "") &&
      !cfg._csrfRetried;
    if (isCsrf) {
      cfg._csrfRetried = true;
      try {
        await axios.post(`${API}/auth/csrf`, null, { withCredentials: true });
        const token = readCookie(CSRF_COOKIE);
        if (token) cfg.headers = { ...(cfg.headers || {}), [CSRF_HEADER]: token };
        return api(cfg);
      } catch (csrfErr) {
        // CSRF bootstrap failed — log (dev only) and fall through to reject the original 403.
        logger.warn("[api] CSRF token refresh failed, propagating original 403:", csrfErr);
      }
    }

    // Pure network failure (no response reached us at all) — most often a
    // Render cold-start or a transient proxy hiccup, not a real API error.
    // One silent retry after a short pause fixes the vast majority of these
    // without the user having to do anything. Only for idempotent-safe
    // auth/reset endpoints, and only once per request.
    const isNetworkError = !error.response;
    const RETRYABLE = ["/auth/reset-password", "/auth/reset-password/otp/verify", "/auth/forgot-password", "/auth/forgot-password/otp", "/auth/2fa/login-verify"];
    const url = cfg.url || "";
    if (isNetworkError && !cfg._networkRetried && RETRYABLE.some((p) => url.includes(p))) {
      cfg._networkRetried = true;
      await new Promise((resolve) => setTimeout(resolve, 2500));
      logger.warn("[api] network error on", url, "— retrying once after cold-start delay");
      return api(cfg);
    }

    return Promise.reject(error);
  }
);

export default api;
